import { encode, decode } from "../../../common/multiplayerCodec.ts";

const query = new URLSearchParams(location.search);
if (query.has("mp")) {
	sessionStorage.setItem("bbgm-multiplayer-room", query.get("mp")!);
}
export const multiplayerRoom = sessionStorage.getItem("bbgm-multiplayer-room");
const tab = crypto.randomUUID();

export class MultiplayerTransport {
	private handler: (data: any) => any = () => undefined;
	private events: EventSource;
	private listeners = new Map<string, ((event: any) => void)[]>();
	private ready: Promise<void>;
	private connectedOnce = false;
	constructor() {
		this.events = new EventSource(
			`/multiplayer/api/events?room=${encodeURIComponent(multiplayerRoom!)}&tab=${tab}`,
		);
		this.ready = new Promise((resolve, reject) => {
			this.events.addEventListener("open", () => resolve(), { once: true });
			setTimeout(
				() =>
					reject(
						new Error(
							"Não foi possível conectar ao servidor multiplayer. Abra o painel e entre novamente.",
						),
					),
				20000,
			);
		});
		this.events.addEventListener("open", () => {
			if (this.connectedOnce) {
				void this.handler([
					"realtimeUpdate",
					["firstRun", "newPhase", "gameSim"],
				]);
			}
			this.connectedOnce = true;
		});
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "visible") {
				void this.handler([
					"realtimeUpdate",
					["firstRun", "newPhase", "gameSim"],
				]);
			}
		});
		this.events.onmessage = async (event) => {
			const packet = JSON.parse(event.data);
			if (packet.refresh) {
				// A different participant changed the league. Recompute the current view.
				void this.handler([
					"realtimeUpdate",
					["firstRun", "gameSim", "playerMovement", "newPhase"],
				]);
				return;
			}
			try {
				const result = await this.handler(decode(packet.data));
				if (packet.id) {
					await fetch("/multiplayer/api/callback", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ tab, id: packet.id, data: encode(result) }),
					});
				}
			} catch (error) {
				if (packet.id) {
					await fetch("/multiplayer/api/callback", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ tab, id: packet.id, error: String(error) }),
					});
				}
			}
		};
		void this.installToolbar();
	}
	register(handler: (data: any) => any) {
		this.handler = handler;
	}
	addEventListener(type: string, handler: (event: any) => void) {
		this.listeners.set(type, [...(this.listeners.get(type) ?? []), handler]);
	}
	async postMessage(data: any) {
		await this.ready;
		const upload = async (value: any): Promise<any> => {
			if (value instanceof Blob) {
				const response = await fetch(
					`/multiplayer/api/upload?room=${encodeURIComponent(multiplayerRoom!)}`,
					{
						method: "POST",
						body: value,
						headers: {
							"Content-Type": "application/octet-stream",
							"X-File-Name": encodeURIComponent(
								value instanceof File ? value.name : "league.json",
							),
						},
					},
				);
				const body = await response.json();
				if (!response.ok) {
					throw new Error(body.error);
				}
				return { __mpUpload: body.id };
			}
			if (Array.isArray(value)) {
				return Promise.all(value.map(upload));
			}
			if (
				value &&
				typeof value === "object" &&
				!(value instanceof Set) &&
				!(value instanceof Map)
			) {
				return Object.fromEntries(
					await Promise.all(
						Object.entries(value).map(async ([k, v]) => [k, await upload(v)]),
					),
				);
			}
			return value;
		};
		const prepared = await upload(data);
		if (prepared[2]?.url) {
			throw new Error(
				"No multiplayer, baixe o arquivo de liga e use a opção de upload do arquivo.",
			);
		}
		const response = await fetch("/multiplayer/api/rpc", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				room: multiplayerRoom,
				tab,
				data: encode(prepared),
			}),
		});
		const body = await response.json();
		if (!response.ok) {
			throw new Error(body.error ?? "Falha na conexão multiplayer.");
		}
		return decode(body.data);
	}
	private async installToolbar() {
		await this.ready;
		const response = await fetch(
			`/multiplayer/api/room?room=${encodeURIComponent(multiplayerRoom!)}`,
		);
		if (!response.ok) {
			location.href = "/multiplayer/";
			return;
		}
		const room = await response.json();
		const bar = document.createElement("div");
		bar.id = "multiplayer-bar";
		bar.style.cssText =
			"position:fixed;bottom:0;left:0;right:0;z-index:1050;background:#12304a;color:white;padding:7px 14px;display:flex;align-items:center;gap:12px;font:14px system-ui;box-shadow:0 -2px 8px #0003";
		const text = document.createElement("span");
		text.textContent = `Multiplayer · ${room.name} · ${room.admin ? "Administrador" : "Participante"}`;
		bar.append(text);
		const panel = document.createElement("a");
		panel.href = "/multiplayer/";
		panel.rel = "external";
		panel.textContent = "Participantes, convites e propostas";
		panel.style.color = "#9ed7ff";
		bar.append(panel);
		const status = document.createElement("small");
		status.textContent = "Conectado";
		bar.append(status);
		this.events.addEventListener("error", () => {
			status.textContent = "Reconectando…";
		});
		this.events.addEventListener("open", () => {
			status.textContent = "Conectado";
		});
		document.body.append(bar);
		document.body.style.paddingBottom = "52px";
		document.addEventListener(
			"click",
			(event) => {
				const a = (event.target as Element).closest("a");
				if (
					!a ||
					a.origin !== location.origin ||
					event.ctrlKey ||
					event.metaKey
				) {
					return;
				}
				if (
					[
						"/",
						"/new_league",
						"/account",
						"/account/login_or_register",
					].includes(a.pathname)
				) {
					event.preventDefault();
					event.stopPropagation();
					location.href = "/multiplayer/";
				}
			},
			true,
		);
	}
}
